import React, { useEffect, useState } from 'react';
import { detectLanguage } from '../translations';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import { getTranslation } from '../translations';
import { supabase } from '../services/supabase';

const ContactPage: React.FC = () => {
  const [lang, setLang] = useState('en');
  const [t, setT] = useState(getTranslation('en'));
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  useEffect(() => {
    const savedLang = localStorage.getItem('preferredLanguage');
    const detectedLang = savedLang || detectLanguage();
    setLang(detectedLang);
    setT(getTranslation(detectedLang));
  }, []);

  const handleLanguageChange = (newLang: string) => {
    setLang(newLang);
    setT(getTranslation(newLang));
    localStorage.setItem('preferredLanguage', newLang);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');
    
    try {
      // CloudFlare Worker를 통한 하이브리드 방식 (이메일 + DB 저장)
      // 환경 감지: dev.aivuebiz.com은 개발, aivuebiz.com은 운영
      const isDev = typeof window !== 'undefined' && 
        (window.location.hostname.includes('dev.') || window.location.hostname === 'localhost');
      
      const workerUrl = isDev
        ? process.env.REACT_APP_CONTACT_WORKER_URL_DEV
        : process.env.REACT_APP_CONTACT_WORKER_URL_PROD;

      if (!workerUrl) {
        // Worker URL이 없으면 Supabase 직접 저장 (폴백)
        const tableName = isDev ? 'D_qna' : 'P_qna';
        
        const { error } = await supabase
          .from(tableName)
          .insert([
            {
              name: formData.name,
              email: formData.email,
              company: formData.company || null,
              subject: formData.subject,
              message: formData.message,
              user_agent: navigator.userAgent
            }
          ])
          .select()
          .single();

        if (error) {
          throw error;
        }
      } else {
        // CloudFlare Worker 호출 (이메일 + DB 저장)
        const response = await fetch(workerUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            name: formData.name,
            email: formData.email,
            company: formData.company,
            subject: formData.subject,
            message: formData.message
          })
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({ error: 'Failed to send message' }));
          throw new Error(errorData.error || 'Failed to send message');
        }

        const result = await response.json();
        console.log('Contact form submitted:', result);
      }

      // 성공
      setSubmitStatus('success');
      setFormData({
        name: '',
        email: '',
        company: '',
        subject: '',
        message: ''
      });
      
      // 성공 메시지를 5초 후 리셋
      setTimeout(() => {
        setSubmitStatus('idle');
      }, 5000);
      
    } catch (err) {
      console.error('Error submitting contact form:', err);
      setSubmitStatus('error');
      
      // 에러 상태를 5초 후 리셋
      setTimeout(() => {
        setSubmitStatus('idle');
      }, 5000);
    } finally {
      setIsSubmitting(false);
    }
  };

  const isKorean = lang === 'ko';

  const contactInfo = [
    {
      icon: '📧',
      title: isKorean ? '이메일' : 'Email',
      content: 'support@aivuebiz.com',
      link: 'mailto:support@aivuebiz.com'
    },
    {
      icon: '📍',
      title: isKorean ? '본사' : 'Headquarters',
      content: isKorean ? '서울특별시 영등포구 국제금융로8길 27-8' : '27-8, Gukjegeumyung-ro 8-gil, Yeongdeungpo-gu, Seoul, Republic of Korea',
      link: null
    },
    {
      icon: '💬',
      title: isKorean ? '실시간 채팅' : 'Live Chat',
      content: isKorean ? '평일 9AM - 6PM (KST)' : 'Weekdays 9AM - 6PM (KST)',
      link: null
    }
  ];

  const faqs = [
    {
      question: isKorean ? '무료 체험이 가능한가요?' : 'Is there a free trial?',
      answer: isKorean 
        ? '네, 무료 플랜(일부 기능 제한)이 포함되어 있습니다. 신용카드 정보 없이 시작할 수 있습니다.'
        : 'Yes, it includes a free plan (with some feature limitations). You can get started without providing credit card information.'
    },
    {
      question: isKorean ? '팀 규모에 제한이 있나요?' : 'Are there team size limits?',
      answer: isKorean
        ? '플랜에 따라 다릅니다. Personal은 개인용, Business는 팀용, Enterprise는 무제한입니다.'
        : 'It depends on the plan. Personal is for individuals, Business is for teams, Enterprise is unlimited.'
    },
    {
      question: isKorean ? '데이터는 안전한가요?' : 'Is my data secure?',
      answer: isKorean
        ? '모든 데이터는 종단 간 암호화되며, 엄격한 보안 프로토콜을 따릅니다.'
        : 'All data is end-to-end encrypted and follows strict security protocols.'
    }
  ];

  return (
    <div className="font-sans antialiased bg-gray-50">
      <Navigation t={t} currentLang={lang} onLanguageChange={handleLanguageChange} />
      
      {/* Hero Section */}
      <section className="pt-32 pb-16 bg-gradient-to-br from-primary-50 via-white to-secondary-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6">
              {isKorean ? '문의하기' : 'Contact Us'}
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              {isKorean
                ? '궁금한 점이 있으신가요? 언제든지 문의해주세요. 빠르게 답변드리겠습니다.'
                : 'Have questions? Feel free to reach out. We\'ll get back to you as soon as possible.'}
            </p>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12">
            
            {/* Contact Form */}
            <div className="bg-white rounded-3xl shadow-xl p-8">
              <h2 className="text-3xl font-bold text-gray-900 mb-6">
                {isKorean ? '메시지 보내기' : 'Send a Message'}
              </h2>
              
              {submitStatus === 'success' && (
                <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
                  <p className="text-green-800">
                    {isKorean 
                      ? '✅ 메시지가 성공적으로 전송되었습니다. 곧 연락드리겠습니다!'
                      : '✅ Message sent successfully. We\'ll get back to you soon!'}
                  </p>
                </div>
              )}
              
              {submitStatus === 'error' && (
                <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-red-800">
                    {isKorean 
                      ? '❌ 메시지 전송에 실패했습니다. 잠시 후 다시 시도해주세요.'
                      : '❌ Failed to send message. Please try again later.'}
                  </p>
                </div>
              )}
              
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {isKorean ? '이름 *' : 'Name *'}
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      placeholder={isKorean ? '홍길동' : 'John Doe'}
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {isKorean ? '이메일 *' : 'Email *'}
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      placeholder="you@example.com"
                    />
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {isKorean ? '회사명' : 'Company'}
                  </label>
                  <input
                    type="text"
                    name="company"
                    value={formData.company}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder={isKorean ? '회사명 (선택사항)' : 'Company name (optional)'}
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {isKorean ? '문의 유형 *' : 'Subject *'}
                  </label>
                  <select
                    name="subject"
                    value={formData.subject}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  >
                    <option value="">{isKorean ? '선택하세요' : 'Select a subject'}</option>
                    <option value="sales">{isKorean ? '구독 문의' : 'Subscription Inquiry'}</option>
                    <option value="support">{isKorean ? '기술 지원' : 'Technical Support'}</option>
                    <option value="advertising">{isKorean ? '광고 문의' : 'Advertising Inquiry'}</option>
                    <option value="partnership">{isKorean ? '파트너십' : 'Partnership'}</option>
                    <option value="other">{isKorean ? '기타' : 'Other'}</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {isKorean ? '메시지 *' : 'Message *'}
                  </label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    required
                    rows={6}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder={isKorean ? '문의 내용을 입력해주세요...' : 'Enter your message...'}
                  />
                </div>
                
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full px-8 py-4 bg-gradient-to-r from-primary-600 to-primary-700 text-white rounded-xl font-semibold hover:shadow-xl transition-all hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting 
                    ? (isKorean ? '전송 중...' : 'Sending...') 
                    : (isKorean ? '메시지 전송' : 'Send Message')}
                </button>
              </form>
            </div>
            
            {/* Contact Info & FAQ */}
            <div className="space-y-8">
              {/* Contact Information */}
              <div>
                <h3 className="text-2xl font-bold text-gray-900 mb-6">
                  {isKorean ? '연락처 정보' : 'Contact Information'}
                </h3>
                <div className="space-y-4">
                  {contactInfo.map((info, index) => (
                    <div key={index} className="flex items-start space-x-4 p-4 bg-gray-50 rounded-xl">
                      <span className="text-3xl">{info.icon}</span>
                      <div>
                        <h4 className="font-semibold text-gray-900 mb-1">{info.title}</h4>
                        {info.link ? (
                          <a href={info.link} className="text-primary-600 hover:text-primary-700">
                            {info.content}
                          </a>
                        ) : (
                          <p className="text-gray-600">{info.content}</p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              
              {/* FAQ Section */}
              <div>
                <h3 className="text-2xl font-bold text-gray-900 mb-6">
                  {isKorean ? '자주 묻는 질문' : 'Frequently Asked Questions'}
                </h3>
                <div className="space-y-4">
                  {faqs.map((faq, index) => (
                    <div key={index} className="border border-gray-200 rounded-xl p-6 hover:shadow-lg transition-shadow">
                      <h4 className="font-semibold text-gray-900 mb-2">{faq.question}</h4>
                      <p className="text-gray-600">{faq.answer}</p>
                    </div>
                  ))}
                </div>
              </div>
              
              {/* Business Hours */}
              <div className="bg-gradient-to-br from-primary-50 to-secondary-50 rounded-2xl p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-4">
                  {isKorean ? '업무 시간' : 'Business Hours'}
                </h3>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-gray-600">{isKorean ? '월-금' : 'Mon-Fri'}</span>
                    <span className="font-semibold text-gray-900">9:00 AM - 6:00 PM (KST)</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">{isKorean ? '토-일' : 'Sat-Sun'}</span>
                    <span className="font-semibold text-gray-900">{isKorean ? '휴무' : 'Closed'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">{isKorean ? '공휴일' : 'Holidays'}</span>
                    <span className="font-semibold text-gray-900">{isKorean ? '휴무' : 'Closed'}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Map Section (Optional) - Temporarily commented out */}
      {/* <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-3xl shadow-xl overflow-hidden">
            <div className="aspect-video bg-gradient-to-br from-primary-100 to-secondary-100 flex items-center justify-center">
              <div className="text-center">
                <svg className="w-24 h-24 text-primary-600 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                <h3 className="text-2xl font-bold text-gray-900 mb-2">Seoul, South Korea</h3>
                <p className="text-gray-600">
                  {isKorean ? '글로벌 혁신의 중심' : 'Center of Global Innovation'}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section> */}

      <Footer t={t} />
    </div>
  );
};

export default ContactPage;